import { useQuery } from '@apollo/client'
import {
  Badge,
  Button,
  ButtonVariant,
  IconButton,
  IconColor,
  IconName,
  IconSize,
  InfoIcon,
  InfoIconColor,
  Input,
  InputVariant,
  SnackbarVariant,
  Status,
  Switch,
  Text,
  TextSize,
  TextWeight,
  TipPlacement,
  Tooltip,
  makeStyles,
  useSnackbar
} from '@focaldata/cin-ui-components'
import { ChangeEvent, useEffect, useState } from 'react'
import {
  defaultSoftLaunchSamplesNeeded,
  isSoftLaunchSamplesValid,
  softLaunchSamplesErrorMessage,
  useGetSurveyOrder
} from '../../../../components/SoftLaunch'
import { GET_FIELDWORK_SOFT_LAUNCH } from '../../../../components/SoftLaunch/SoftLaunch.questionnaire.query'
import { stringToStatusMap } from '../../../../controls/ProjectListItem/ProjectListItem.utils'
import { Survey, SurveyState } from '../../../../data/gql-gen/dashboard/graphql'
import { useUpdateFieldworkV2 } from '../../../../hooks/audience/createOrUpdateFieldworkV2'
import { useFullLaunch } from '../../../../modules/Results/FullLaunchButton'
import { FullLaunchDialogContainer } from '../../../../modules/Results/FullLaunchDialog'
import EndMarket from '../../../../modules/Survey/EndMarket'
import { usePauseResumeSurvey } from '../../../../modules/Survey/usePauseResumeSurvey'
import {
  formatThousandsWithCommas,
  getLanguageLabel,
  handleShowPreview
} from '../../../../utils/HelperFunctions'
import { PositiveIntegerNumberFormatCustom } from '../../../../utils/NumberFormatCustom'
import { PanelSupplier } from '../../../../utils/audienceHelper'
import { parseMarketName } from '../../../../utils/marketFlags'
import { MarketLaunchDialog } from './MarketLaunchDialog'

// Soft launch is only offered for panel suppliers that support it (matches the
// single-survey ConfirmationSoftLaunch gating).
const SOFT_LAUNCH_SUPPLIERS = [PanelSupplier.CINT, PanelSupplier.PURESPECTRUM]

const MULTI_LANGUAGE_SOFT_LAUNCH_MESSAGE =
  'Soft launch is not available for multi-language markets.'

const isLaunchableState = (state: SurveyState): boolean =>
  state === SurveyState.Draft || state === SurveyState.Scheduled

const useStyles = makeStyles()({
  softLaunchCell: { display: 'flex', alignItems: 'center', gap: 8 },
  completesInput: { width: 80 },
  // Live rows can offer up to three actions (Full launch / Pause / End); let
  // them wrap inside the cell rather than blow the grid column out.
  actions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }
})

interface MarketRowProps {
  className: string
  projectId: string
  survey: Survey
  // Resolved label for the base market, whose name may be a legacy project
  // name; defaults to the market's own name (trailing "(XX)" stripped).
  displayName?: string
  questionsCount: number
  target: number
  languages: string[]
  panelSupplier?: PanelSupplier
  hasIncompleteTranslations: boolean
  // True until the project's first market launches — the launch dialog warns
  // that the shared questionnaire locks at that point.
  isFirstLaunch: boolean
  onSurveyChanged: () => void | Promise<unknown>
}

/**
 * One market row of the Confirm table. Lives in its own component so the
 * per-market hooks (soft-launch query, order state, pause/resume/end, full
 * launch) can run per row.
 */
export const MarketRow = ({
  className,
  projectId,
  survey,
  displayName,
  questionsCount,
  target,
  languages,
  panelSupplier,
  hasIncompleteTranslations,
  isFirstLaunch,
  onSurveyChanged
}: MarketRowProps) => {
  const { classes } = useStyles()
  const marketName = displayName ?? parseMarketName(survey.name).label
  const [isLaunchDialogOpen, setIsLaunchDialogOpen] = useState(false)
  const [isFullLaunchDialogOpen, setIsFullLaunchDialogOpen] = useState(false)
  const [isEndDialogOpen, setIsEndDialogOpen] = useState(false)
  // Covers the whole persist (mutation + refetch), unlike
  // updateFieldworkLoading, so the controls stay disabled until the refetched
  // value has landed and can't clobber a newer edit.
  const [isPersisting, setIsPersisting] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const {
    pause,
    resume,
    loading: pauseResumeLoading
  } = usePauseResumeSurvey(onSurveyChanged)
  const { updateFieldworkV2, updateFieldworkLoading } = useUpdateFieldworkV2()

  const showSoftLaunch =
    panelSupplier !== undefined && SOFT_LAUNCH_SUPPLIERS.includes(panelSupplier)
  // Multi-language markets route through a multi-locale launch path that
  // ignores soft launch, so the option must not be offered.
  const isMultiLanguage = languages.length > 1

  const { data: softLaunchData, refetch: refetchSoftLaunch } = useQuery(
    GET_FIELDWORK_SOFT_LAUNCH,
    {
      context: { clientName: 'questionnaire' },
      variables: { surveyId: survey.surveyId },
      fetchPolicy: 'cache-and-network',
      skip: !showSoftLaunch
    }
  )

  // Soft launch is "on" when a soft-launch sample size is configured on the
  // fieldwork. The server `isConfiguredForSoftLaunch` field reflects a placed
  // soft-launch *order* (false for a draft), so it never tracks the toggle —
  // derive from softLaunch.samplesNeeded, matching useGetSoftLaunch.
  const softLaunchSamplesNeeded =
    softLaunchData?.fieldwork?.softLaunch?.samplesNeeded ?? 0
  const isSoftLaunchOn = softLaunchSamplesNeeded > 0
  const adjustedTarget =
    softLaunchData?.fieldwork?.samplesNeeded.adjusted ?? target
  // Soft launch is configured pre-launch; don't allow toggling once fielding.
  const softLaunchDisabled =
    !isLaunchableState(survey.state) || updateFieldworkLoading || isPersisting

  // Local draft of the completes input so it stays editable while typing;
  // re-synced whenever the persisted value changes (controlled value — the
  // row's cache-and-network query can resolve after mount, so an uncontrolled
  // defaultValue would go stale).
  const [completesDraft, setCompletesDraft] = useState<number | ''>('')
  const [hasCompletesError, setHasCompletesError] = useState(false)
  useEffect(() => {
    setCompletesDraft(
      softLaunchSamplesNeeded > 0 ? softLaunchSamplesNeeded : ''
    )
    setHasCompletesError(false)
  }, [softLaunchSamplesNeeded])

  const persistSoftLaunch = async (samplesNeeded: number) => {
    if (!panelSupplier || isMultiLanguage) return
    setIsPersisting(true)
    try {
      const result = await updateFieldworkV2({
        variables: {
          input: {
            surveyId: survey.surveyId,
            panelSupplierCode: panelSupplier,
            softLaunch: { samplesNeeded }
          }
        }
      })
      // The mutation swallows errors via onError (the promise resolves with no
      // data), so failures must be detected here — surface them and restore
      // the last persisted value rather than leaving a value that never saved.
      if (!result.data) {
        enqueueSnackbar('Failed to update the soft launch. Please try again.', {
          variant: SnackbarVariant.Error
        })
        setCompletesDraft(
          softLaunchSamplesNeeded > 0 ? softLaunchSamplesNeeded : ''
        )
      }
      // The update mutation's fieldwork fragment doesn't select softLaunch, so
      // the cache only refreshes via an explicit refetch.
      await refetchSoftLaunch()
    } finally {
      setIsPersisting(false)
    }
  }

  const handleToggleSoftLaunch = async () => {
    if (isMultiLanguage) return
    const samplesNeeded = isSoftLaunchOn
      ? 0
      : defaultSoftLaunchSamplesNeeded(adjustedTarget)
    setCompletesDraft(samplesNeeded > 0 ? samplesNeeded : '')
    setHasCompletesError(false)
    await persistSoftLaunch(samplesNeeded)
  }

  const handleCompletesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setCompletesDraft(value ? Number(value) : '')
  }

  // Persist on blur with the single-market bounds (1..adjusted target); out of
  // range keeps the last persisted value on the server and flags the input.
  const handleCompletesBlur = async () => {
    const value = Number(completesDraft)
    const isValid =
      completesDraft !== '' && isSoftLaunchSamplesValid(value, adjustedTarget)
    setHasCompletesError(!isValid)
    if (isValid && value !== softLaunchSamplesNeeded) {
      await persistSoftLaunch(value)
    }
  }

  const isPaused = survey.state === SurveyState.Paused

  // The order state is consumed only by canFullLaunch below, which needs a Live
  // or Paused market — skip the query for every other state (Draft/Scheduled
  // have no order at all; Completed/Halted rows never offer Full launch).
  const { isSoftLaunch, isFullLaunch, panelSupplierOrders } = useGetSurveyOrder(
    survey.surveyId,
    { skip: survey.state !== SurveyState.Live && !isPaused }
  )
  const { confirmFullLaunch, isPlacingOrder, isOrderPlaced } = useFullLaunch({
    surveyId: survey.surveyId,
    panelSupplierCode: panelSupplierOrders?.[0]?.panelSupplierCode,
    samplesNeeded: adjustedTarget,
    remainingSamplesNeeded: Math.max(
      adjustedTarget - softLaunchSamplesNeeded,
      0
    ),
    isPaused,
    onCompleted: async () => {
      setIsFullLaunchDialogOpen(false)
      await onSurveyChanged()
    }
  })
  // The order sizes above come from the row's fieldwork query, which resolves
  // independently of the order query — don't offer Full launch until that data
  // is actually loaded, or the mutations would fire with 0/unsubtracted
  // completes (matches the single-survey gate, which requires the soft-launch
  // fieldwork config to be resolved before the button shows).
  const fieldworkNumbersLoaded =
    !!softLaunchData?.fieldwork && softLaunchSamplesNeeded > 0
  // Paused counts too — the launch resumes the market, so Resume first isn't
  // needed.
  const canFullLaunch =
    (survey.state === SurveyState.Live || isPaused) &&
    !!isSoftLaunch &&
    !isFullLaunch &&
    !isOrderPlaced &&
    fieldworkNumbersLoaded

  const renderAction = () => {
    switch (survey.state) {
      case SurveyState.Draft:
      case SurveyState.Scheduled:
        return (
          <Button
            variant={ButtonVariant.PrimaryFilled}
            // Block launching while the soft-launch completes input is invalid
            // (the same state that shows the red error icon) — an out-of-range
            // value never persists, so launching would field the last saved
            // value and silently ignore the edit on screen.
            disabled={
              hasIncompleteTranslations ||
              questionsCount === 0 ||
              hasCompletesError
            }
            onClick={() => setIsLaunchDialogOpen(true)}
          >
            Launch
          </Button>
        )
      case SurveyState.Live:
        return (
          <>
            {canFullLaunch && (
              <Button
                variant={ButtonVariant.PrimaryFilled}
                disabled={isPlacingOrder || isFullLaunchDialogOpen}
                onClick={() => setIsFullLaunchDialogOpen(true)}
              >
                Full launch
              </Button>
            )}
            <Button
              variant={ButtonVariant.PrimaryOutlined}
              // Also disabled while a full-launch order is in flight — the
              // dialog closes after the first PURESPECTRUM mutation while the
              // follow-up order is still pending, and pausing/ending mustn't
              // race it.
              disabled={pauseResumeLoading || isPlacingOrder}
              onClick={() => pause(survey.surveyId)}
            >
              Pause
            </Button>
            <Button
              variant={ButtonVariant.SecondaryOutlined}
              disabled={pauseResumeLoading || isPlacingOrder}
              onClick={() => setIsEndDialogOpen(true)}
            >
              End
            </Button>
          </>
        )
      case SurveyState.Paused:
        return (
          <>
            {canFullLaunch && (
              <Button
                variant={ButtonVariant.PrimaryFilled}
                disabled={
                  isPlacingOrder || isFullLaunchDialogOpen || pauseResumeLoading
                }
                onClick={() => setIsFullLaunchDialogOpen(true)}
              >
                Full launch
              </Button>
            )}
            <Button
              variant={ButtonVariant.PrimaryFilled}
              disabled={pauseResumeLoading || isPlacingOrder}
              onClick={() => resume(survey.surveyId)}
            >
              Resume
            </Button>
            <Button
              variant={ButtonVariant.SecondaryOutlined}
              disabled={pauseResumeLoading || isPlacingOrder}
              onClick={() => setIsEndDialogOpen(true)}
            >
              End
            </Button>
          </>
        )
      default:
        return <Text size={TextSize.ms}>—</Text>
    }
  }

  return (
    <div className={className}>
      <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
        {marketName}
      </Text>
      <Text size={TextSize.ms}>
        {languages.length > 0
          ? languages.map(getLanguageLabel).join(', ')
          : '—'}
      </Text>
      <Text size={TextSize.ms}>{questionsCount}</Text>
      <Text size={TextSize.ms}>{formatThousandsWithCommas(target)}</Text>
      <span className={classes.softLaunchCell}>
        {showSoftLaunch && isMultiLanguage ? (
          <Tooltip
            title={MULTI_LANGUAGE_SOFT_LAUNCH_MESSAGE}
            tipPlacement={TipPlacement.Top}
          >
            <Switch
              checked={false}
              onChange={() => undefined}
              disabled
              first
              last
            />
          </Tooltip>
        ) : showSoftLaunch ? (
          <>
            <Switch
              checked={isSoftLaunchOn}
              onChange={handleToggleSoftLaunch}
              disabled={softLaunchDisabled}
              first
              last
            />
            {isSoftLaunchOn && (
              <>
                <Input
                  className={classes.completesInput}
                  ariaLabel="Soft launch completes"
                  variant={InputVariant.Filled}
                  value={completesDraft}
                  onChange={handleCompletesChange}
                  onBlur={handleCompletesBlur}
                  customFormat={PositiveIntegerNumberFormatCustom}
                  disabled={softLaunchDisabled}
                />
                {hasCompletesError && (
                  <InfoIcon
                    style={{ marginRight: 8 }}
                    paddingLeft={false}
                    iconColor={InfoIconColor.Error}
                    text={softLaunchSamplesErrorMessage(adjustedTarget)}
                  />
                )}
              </>
            )}
          </>
        ) : (
          <Text size={TextSize.ms}>—</Text>
        )}
      </span>
      <span>
        <Badge
          label={survey.state}
          status={stringToStatusMap.get(survey.state) ?? Status.DRAFT}
        />
      </span>
      <span>
        <Tooltip title="Preview" tipPlacement={TipPlacement.Bottom}>
          <IconButton
            smallHoverShadowSize
            iconName={IconName.Visibility}
            iconSize={IconSize.Large}
            iconColor={IconColor.Background}
            ariaLabel="Preview"
            onClick={() =>
              handleShowPreview(
                survey.surveyId,
                panelSupplier ?? PanelSupplier.PURESPECTRUM
              )
            }
          />
        </Tooltip>
      </span>
      <span className={classes.actions}>{renderAction()}</span>
      <MarketLaunchDialog
        open={isLaunchDialogOpen}
        projectId={projectId}
        surveyId={survey.surveyId}
        marketName={marketName}
        isFirstLaunch={isFirstLaunch}
        onClose={() => setIsLaunchDialogOpen(false)}
        onLaunched={onSurveyChanged}
      />
      <FullLaunchDialogContainer
        showDialog={isFullLaunchDialogOpen}
        toggleOpenDialog={setIsFullLaunchDialogOpen}
        onConfirm={confirmFullLaunch}
        isPlacingOrder={isPlacingOrder}
      />
      <EndMarket
        isOpen={isEndDialogOpen}
        toggleIsOpen={setIsEndDialogOpen}
        surveyId={survey.surveyId}
      />
    </div>
  )
}
